import type { OverviewResponse } from "./overview"
import { v2DesignOn } from "@/datox/v2/design-flag"

export type DatoxProject = {
  id: string
  company_id: string
  owner_user_id: string
  created_by_user_name?: string | null
  created_by_user_email?: string | null
  name: string
  description?: string | null
  status: string
  default_period_id?: string | null
  project_role: "viewer" | "editor" | "owner"
  created_at: string
  updated_at: string
}

export type DatoxCompany = {
  id: string
  name: string
  status: string
  role: string
}

export type DatoxSession = {
  id: string
  project_id: string
  period_id?: string | null
  title: string
  status: string
  remote_opencode_session_id?: string | null
  session_metadata?: { workflow_case_id?: string; [key: string]: unknown }
  created_at: string
  updated_at: string
}

export type DatoxPeriod = {
  id: string
  company_id: string
  project_id: string
  created_by_user_id?: string | null
  created_by_user_name?: string | null
  created_by_user_email?: string | null
  name: string
  status: string
  period_metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type DatoxMessage = {
  id: string
  project_id: string
  session_id: string
  role: "user" | "assistant" | "system" | "tool"
  content: string
  structured_payload?: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type DatoxActivityLog = {
  id: string
  kind: "audit" | "message"
  event_type: string
  title: string
  description?: string | null
  project_id?: string | null
  project_name?: string | null
  period_id?: string | null
  period_name?: string | null
  session_id?: string | null
  session_title?: string | null
  user_id?: string | null
  user_email?: string | null
  user_name?: string | null
  payload: Record<string, unknown>
  details: Record<string, string>
  created_at: string
}

export type DatoxUsageStats = {
  projects: number
  periods: number
  chats: number
  sessions: number
  agents: number
  files: number
  extractions: number
  transformations: number
  output_tables: number
  reports: number
  approvals: number
  input_tokens: number
  output_tokens: number
  total_tokens: number
  last_updated_at: string
  by_project: Array<Record<string, unknown>>
  by_period: Array<Record<string, unknown>>
  recent_runs: Array<Record<string, unknown>>
  recent_activity: Array<Record<string, unknown>>
  file_stats: Record<string, unknown>
  efficiency: Record<string, unknown>
  data_sources: Array<Record<string, unknown>>
}

export type DatoxFile = {
  case_id?: string | null
  id: string
  project_id: string
  period_id?: string | null
  filename: string
  content_type: string
  size: number
  status: string
  parsed_metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type DatoxFilePart = {
  id: string
  file_id: string
  name: string
  part_type: string
  index: number
  preview_text?: string | null
  part_metadata: Record<string, unknown>
}

export type DatoxFileDetail = DatoxFile & {
  parts: DatoxFilePart[]
}

export type DatoxSessionContext = {
  project_id: string
  project_name?: string | null
  period_id?: string | null
  period_name?: string | null
  session_id: string
  remote_opencode_session_id: string
  title: string
  session_metadata: Record<string, unknown>
  workflow_case?: Record<string, unknown> | null
  report?: { name: string; workspace_id?: string | null; dashboard_id?: string | null; target_id?: string | null } | null
}

export type DatoxProjectMember = {
  id: string
  company_id: string
  project_id: string
  user_id: string
  user_email?: string | null
  user_name?: string | null
  role: "viewer" | "editor" | "owner"
  status: string
  invited_by_user_id?: string | null
  created_at: string
  updated_at: string
}

export type DatoxCompanyAdmin = {
  user_id: string
  user_email: string | null
  user_name: string | null
  role: "admin" | "owner"
}

export type DatoxArtifact = {
  revision?: number
  revision_count?: number
  case_id?: string | null
  id: string
  company_id: string
  project_id: string
  period_id?: string | null
  session_id?: string | null
  workflow_run_id?: string | null
  artifact_type: string
  format: string
  title: string
  preview_html?: string | null
  artifact_metadata: Record<string, unknown>
  status: string
  created_at: string
  updated_at: string
}

export function isDashboardShareArtifact(artifact: DatoxArtifact) {
  return artifact.artifact_metadata?.source === "dashboard_share"
}

export type DatoxDashboard = {
  revision?: number
  revision_count?: number
  revision_tables?: { datasetID: string; name: string; headers: string[]; rows: Record<string, string | number | boolean | null>[] }[] | null
  revision_datasets?: DatoxDashboardDataset[] | null
  kpi_values?: Record<string, DatoxKpiResult>
  kpi_source_keys?: Record<string, string>
  case_id?: string | null
  id: string
  company_id: string
  project_id: string
  period_id?: string | null
  name: string
  description?: string | null
  status: string
  draft_config: Record<string, unknown>
  published_config?: Record<string, unknown> | null
  dashboard_metadata: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type DatoxDashboardDatasetColumn = {
  name: string
  type: string
}

export type DatoxDashboardDataset = {
  case_id?: string | null
  workflow_run_id?: string | null
  id: string
  name: string
  source: string
  transformation_id: string
  table_index: number
  columns: DatoxDashboardDatasetColumn[]
  row_count: number
}

export type DatoxDashboardDatasetPreview = {
  dataset_id: string
  name: string
  columns: string[]
  rows: Array<Record<string, string | number | boolean | null>>
  row_count: number
}

export type DatoxDashboardSourceExplanation = {
  title: string
  summary_markdown: string
  formula_markdown?: string | null
  dataset_id: string | null
  dataset_name: string
  filters: Record<string, unknown>[]
  sources: Array<{
    source_file_id?: string | null
    source_file_name?: string | null
    source_extracted_table?: string | null
    sample_rows: Array<Record<string, unknown>>
    evidence: Array<{ quote?: string | null; page?: number | null; sheet?: string | null; cell_range?: string | null; row?: number | null }>
  }>
  warnings: string[]
}

export type DatoxExtraction = {
  case_id?: string | null
  id: string
  project_id: string
  period_id?: string | null
  file_id?: string | null
  workflow_run_id?: string | null
  schema_name?: string | null
  prompt?: string | null
  result_json: Record<string, unknown>
  confidence?: number | null
  status: string
  created_at: string
  updated_at: string
}

export type DatoxTransformation = {
  case_id?: string | null
  id: string
  project_id: string
  period_id?: string | null
  workflow_run_id?: string | null
  instructions: string
  source_extraction_ids: string[]
  steps_json: Array<Record<string, unknown>>
  output_json: Record<string, unknown>
  status: string
  created_at: string
  updated_at: string
}

export type DatoxApproval = {
  id: string
  project_id: string
  report_artifact_id: string
  requested_by_user_id: string
  approver_email: string
  status: string
  comments?: string | null
  created_at: string
  updated_at: string
}

export type DatoxCatalogApproval = DatoxApproval & {
  project_name: string
  period_id: string | null
  period_name: string | null
  case_id: string | null
  report_title: string
  report_format: string
}

export type DatoxApprovalReview = {
  approval_id: string
  approver_email: string
  requested_by_user_id: string
  requested_by_email?: string | null
  requested_by_name?: string | null
  status: string
  comments?: string | null
  report_title: string
  report_format: string
  report_content_type: string
  report_filename: string
  report_content?: string | null
  artifact_metadata?: Record<string, unknown>
  created_at: string
  updated_at: string
}

export type DatoxConnectSession = {
  token: string
  connect_link: string
  expires_at: string
}

export type DatoxIntegrationConnection = {
  connection_id: string
  provider_config_key: string
  capabilities: Array<"file_import">
  provider_key?: string | null
  provider?: string | null
  end_user?: Record<string, unknown> | null
  metadata?: Record<string, unknown> | null
  created_at?: string | null
  updated_at?: string | null
}

export type DatoxCloudFile = {
  id: string
  name: string
  mime_type?: string | null
  size?: number | null
  is_folder: boolean
  drive_id?: string | null
  parent_id?: string | null
  path?: string | null
  version?: string | null
  modified_at?: string | null
  raw?: Record<string, unknown> | null
}

export type DatoxStreamEvent =
  | { type: "user_message"; message_id: string }
  | { type: "assistant_message"; message_id: string }
  | { type: "token"; content: string }
  | { type: "text_snapshot"; content: string }
  | { type: "action_required" }
  | { type: "run_progress"; steps?: Array<{ label: string; status: string }> }
  | { type: "tool_start"; tool: string; message: string }
  | { type: "final_answer"; content: string }
  | { type: "error"; error: string }

let tokenProvider: ((forceRefresh?: boolean) => Promise<string>) | undefined

export function setDatoxAuthTokenProvider(provider: (forceRefresh?: boolean) => Promise<string>) {
  tokenProvider = provider
}

const jsonHeaders = async () => ({
  ...(await authHeaders()),
  "Content-Type": "application/json",
})

export function datoxApiBaseUrl() {
  return (import.meta.env.VITE_DATOX_AGENT_API_URL || "http://localhost:8088").replace(/\/$/, "")
}

export async function datoxAuthHeaders() {
  return authHeaders()
}

export function datoxAgentMode() {
  return import.meta.env.VITE_DATOX_AGENT_MODE === "true"
}

// The new Lesha Bank design (Compliance V2 look). On by default in agent mode; `?design=classic` or the account menu's
// "New design" switch goes back to Bobur's classic app, and VITE_DATOX_V2_DESIGN=false makes classic the build's
// default (datox/v2/design-flag.ts).
export function datoxV2Design() {
  return v2DesignOn()
}

export function datoxVersion() {
  return "1.0.0"
}

export async function listProjects() {
  return requestJson<DatoxProject[]>("/api/projects")
}

export function getOverview(query: string) {
  return requestJson<OverviewResponse>(`/api/overview?${query}`)
}

export async function getCurrentUser() {
  return requestJson<{ user_id: string; email: string | null; company_id: string; company_role: string; is_datox_admin: boolean }>("/api/me")
}

export async function listCompanyAdmins(companyID: string) {
  return requestJson<DatoxCompanyAdmin[]>(`/api/companies/${encodeURIComponent(companyID)}/admins`)
}

export async function inviteCompanyAdmin(companyID: string, email: string) {
  return requestJson<DatoxCompanyAdmin>(`/api/companies/${encodeURIComponent(companyID)}/admins`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({ email }),
  })
}

export async function listCompanies() {
  return requestJson<DatoxCompany[]>("/api/companies")
}

export function listManagedCompanies() {
  return requestJson<DatoxCompany[]>("/api/admin/companies")
}

export async function createCompany(name: string) {
  return requestJson<DatoxCompany>("/api/admin/companies", {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({ name }),
  })
}

export async function updateCompany(companyID: string, changes: { name?: string; status?: "active" }) {
  return requestJson<DatoxCompany>(`/api/admin/companies/${encodeURIComponent(companyID)}`, {
    method: "PATCH",
    headers: await jsonHeaders(),
    body: JSON.stringify(changes),
  })
}

export async function archiveCompany(companyID: string) {
  const response = await fetch(`${datoxApiBaseUrl()}/api/admin/companies/${encodeURIComponent(companyID)}`, {
    method: "DELETE",
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await response.text())
}

export async function listActivityLogs(options?: { kind?: string; search?: string; limit?: number }) {
  const params = new URLSearchParams()
  if (options?.kind) params.set("kind", options.kind)
  if (options?.search) params.set("search", options.search)
  if (options?.limit) params.set("limit", String(options.limit))
  return requestJson<DatoxActivityLog[]>(`/api/activity${params.size ? `?${params.toString()}` : ""}`)
}

export async function getUsageStats() {
  return requestJson<DatoxUsageStats>("/api/usage/stats")
}

export async function createProject(name: string) {
  return requestJson<DatoxProject>("/api/projects", {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({ name }),
  })
}

export async function updateProject(projectID: string, name: string) {
  return requestJson<DatoxProject>(`/api/projects/${projectID}`, {
    method: "PATCH",
    headers: await jsonHeaders(),
    body: JSON.stringify({ name }),
  })
}

export async function deleteProject(projectID: string) {
  const response = await fetch(`${datoxApiBaseUrl()}/api/projects/${projectID}`, {
    method: "DELETE",
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await response.text())
}

export async function listSessions(projectID: string, periodID?: string) {
  const query = periodID ? `?period_id=${encodeURIComponent(periodID)}` : ""
  return requestJson<DatoxSession[]>(`/api/projects/${projectID}/sessions${query}`)
}

export async function getSession(projectID: string, sessionID: string) {
  return requestJson<DatoxSession>(`/api/projects/${encodeURIComponent(projectID)}/sessions/${encodeURIComponent(sessionID)}`)
}

export async function listPeriods(projectID: string) {
  return requestJson<DatoxPeriod[]>(`/api/projects/${projectID}/periods`)
}

export async function createPeriod(projectID: string, name: string, metadata: Record<string, string | number> = {}) {
  return requestJson<DatoxPeriod>(`/api/projects/${projectID}/periods`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({ name, metadata }),
  })
}

export async function deletePeriod(projectID: string, periodID: string) {
  const response = await fetch(`${datoxApiBaseUrl()}/api/projects/${projectID}/periods/${periodID}`, {
    method: "DELETE",
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await response.text())
}

export async function createSession(projectID: string, title: string, periodID?: string, metadata?: Record<string, unknown>) {
  return requestJson<DatoxSession>(`/api/projects/${projectID}/sessions`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({ title, period_id: periodID, metadata }),
  })
}

export async function ensureRemoteSession(projectID: string, sessionID: string) {
  return requestJson<DatoxSession>(`/api/projects/${projectID}/sessions/${sessionID}/remote`, {
    method: "POST",
    headers: await jsonHeaders(),
  })
}

export type ReportChatScope = {
  workspace_id?: string
  dashboard_id?: string
  target_id?: string
  selection?: { pageID: string; widgetID?: string }
}

export async function setSessionReportContext(projectID: string, sessionID: string, scope: ReportChatScope) {
  return requestJson<DatoxSession>(`/api/projects/${projectID}/sessions/${sessionID}/report-context`, {
    method: "PUT", headers: await jsonHeaders(), body: JSON.stringify(scope),
  })
}

export async function openReportChat(projectID: string, scope: ReportChatScope, newChat = false) {
  return requestJson<DatoxSession>(`/api/projects/${projectID}/sessions/report?new=${newChat}`, {
    method: "POST", headers: await jsonHeaders(), body: JSON.stringify(scope),
  })
}

export async function prepareReportPrompt(remoteSessionID: string, workflowRunID: string, scope?: ReportChatScope) {
  return requestJson<{ text: string | null }>(`/api/sessions/remote/${remoteSessionID}/prompt-context`, {
    method: "POST", headers: await jsonHeaders(), body: JSON.stringify({ scope, workflow_run_id: workflowRunID }),
  })
}

export async function openPeriodWorkflowChat(projectID: string, periodID: string) {
  return requestJson<DatoxSession>(`/api/projects/${projectID}/sessions/workflow?${new URLSearchParams({ period_id: periodID })}`, {
    method: "POST",
    headers: await jsonHeaders(),
  })
}

export async function syncRemoteSessionMessages(remoteSessionID: string) {
  return requestJson<{ synced: number }>(`/api/sessions/remote/${remoteSessionID}/sync`, {
    method: "POST",
    headers: await jsonHeaders(),
  })
}

export async function deleteDatoxSession(projectID: string, sessionID: string) {
  const response = await fetch(`${datoxApiBaseUrl()}/api/projects/${projectID}/sessions/${sessionID}`, {
    method: "DELETE",
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await response.text())
}

export async function createIntegrationConnectSession(providerKey: string) {
  return requestJson<DatoxConnectSession>(`/api/integrations/connect-session`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({ allowed_integrations: [providerKey] }),
  })
}

export async function listIntegrationConnections(providerKey?: string) {
  const query = providerKey ? `?provider_key=${encodeURIComponent(providerKey)}` : ""
  return requestJson<{ connections: DatoxIntegrationConnection[] }>(`/api/integrations/connections${query}`)
}

export async function deleteIntegrationConnection(providerKey: string, connectionID: string) {
  const response = await fetch(`${datoxApiBaseUrl()}/api/integrations/connections?provider_key=${encodeURIComponent(providerKey)}&connection_id=${encodeURIComponent(connectionID)}`, {
    method: "DELETE",
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await response.text())
}

export async function listIntegrationFiles(input: { providerKey: string; connectionID: string; query?: string; cursor?: string; path?: string; folderID?: string }) {
  const params = new URLSearchParams({ provider_key: input.providerKey, connection_id: input.connectionID })
  if (input.query) params.set("query", input.query)
  if (input.cursor) params.set("cursor", input.cursor)
  if (input.path) params.set("path", input.path)
  if (input.folderID) params.set("folder_id", input.folderID)
  return requestJson<{ items: DatoxCloudFile[]; next_cursor?: string | null }>(`/api/integrations/files?${params.toString()}`)
}

export async function importIntegrationFiles(input: {
  projectID: string
  periodID?: string | null
  sessionID?: string | null
  providerKey: string
  connectionID: string
  files: DatoxCloudFile[]
}) {
  return requestJson<{ imported_count: number; skipped_count: number; results: Array<{ external_file_id: string; status: string; file_id?: string | null; file_name?: string | null; reason?: string | null }> }>(
    `/api/integrations/files/import`,
    {
      method: "POST",
      headers: await jsonHeaders(),
      body: JSON.stringify({
        project_id: input.projectID,
        period_id: input.periodID,
        session_id: input.sessionID,
        provider_key: input.providerKey,
        connection_id: input.connectionID,
        files: input.files.map((file) => ({
          external_file_id: file.id,
          name: file.name,
          mime_type: file.mime_type,
          size: file.size,
          path: file.path,
          version: file.version,
        })),
      }),
    },
  )
}

export function buildIntegrationConnectUrl(session: DatoxConnectSession) {
  try {
    const source = new URL(session.connect_link)
    const sessionToken = source.searchParams.get("session_token")
    if (!sessionToken) return session.connect_link
    const connectUiBase = "https://connect.integrations.datox.ai"
    const target = new URL(connectUiBase)
    const apiURL = source.hostname.startsWith("connect.") ? "https://integrations.datox.ai" : source.origin
    target.searchParams.set("session_token", sessionToken)
    target.searchParams.set("apiURL", apiURL)
    return target.toString()
  } catch {
    return session.connect_link
  }
}

export async function listMessages(projectID: string, sessionID: string) {
  return requestJson<DatoxMessage[]>(`/api/projects/${projectID}/sessions/${sessionID}/messages`)
}

export async function listFiles(projectID: string, periodID?: string, caseID?: string, includeUnassigned = false) {
  const scope = workflowScopeQuery(periodID, caseID)
  const query = includeUnassigned ? `${scope}${scope ? "&" : "?"}include_unassigned=true` : scope
  return requestJson<DatoxFile[]>(`/api/projects/${projectID}/files${query}`)
}

export type DatoxFileIntegration = { id: string; name: string; provider: "alphadesk" }
export type DatoxFileSync = {
  id: string
  integration_id: string
  project_id: string
  period_id: string
  status: "queued" | "discovering" | "syncing" | "completed" | "partially_failed" | "failed"
  matched: number
  imported: number
  skipped: number
  failed: number
  error?: string | null
  updated_at: string
  items: { id: string; filename: string; status: string; error?: string | null }[]
}

export function listFileIntegrations(projectID: string) {
  return requestJson<DatoxFileIntegration[]>(`/api/projects/${encodeURIComponent(projectID)}/file-integrations`)
}

export async function syncIntegrationFiles(projectID: string, periodID: string, integrationID: string, requestID: string) {
  return requestJson<DatoxFileSync>(`${fileSyncScope(projectID, periodID)}/file-integrations/${encodeURIComponent(integrationID)}/sync`, {
    method: "POST", headers: await jsonHeaders(), body: JSON.stringify({ request_id: requestID }),
  })
}

export function getIntegrationSync(projectID: string, periodID: string, syncID: string) {
  return requestJson<DatoxFileSync>(`${fileSyncScope(projectID, periodID)}/file-integration-syncs/${encodeURIComponent(syncID)}`)
}

export function getActiveIntegrationSync(projectID: string, periodID: string, integrationID: string) {
  return requestJson<DatoxFileSync | null>(`${fileSyncScope(projectID, periodID)}/file-integrations/${encodeURIComponent(integrationID)}/syncs/active`)
}

export async function retryIntegrationSync(projectID: string, periodID: string, syncID: string) {
  return requestJson<DatoxFileSync>(`${fileSyncScope(projectID, periodID)}/file-integration-syncs/${encodeURIComponent(syncID)}/retry`, {
    method: "POST", headers: await jsonHeaders(),
  })
}

function fileSyncScope(projectID: string, periodID: string) {
  return `/api/projects/${encodeURIComponent(projectID)}/periods/${encodeURIComponent(periodID)}`
}

export async function getFile(projectID: string, fileID: string) {
  return requestJson<DatoxFileDetail>(`/api/projects/${projectID}/files/${fileID}`)
}

export async function getFileContentBlob(projectID: string, fileID: string) {
  const response = await fetch(`${datoxApiBaseUrl()}/api/projects/${projectID}/files/${fileID}/content`, {
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await response.text())
  return response.blob()
}

export async function deleteFile(projectID: string, fileID: string) {
  const response = await fetch(`${datoxApiBaseUrl()}/api/projects/${projectID}/files/${fileID}`, {
    method: "DELETE",
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await response.text())
}

export async function listProjectMembers(projectID: string) {
  return requestJson<DatoxProjectMember[]>(`/api/projects/${projectID}/members`)
}

export async function listArtifacts(projectID: string) {
  return requestJson<DatoxArtifact[]>(`/api/projects/${projectID}/artifacts`)
}

export async function getArtifact(projectID: string, artifactID: string, revision?: number) {
  return requestJson<DatoxArtifact>(`/api/projects/${projectID}/artifacts/${artifactID}${revision ? `?revision=${revision}` : ""}`)
}

export type DatoxOutputRevision = { id: string; output_id: string; output_type: "report" | "dashboard"; number: number; status: string; created_at: string; created_by?: string | null }

export type DatoxKpiComparisonOptionWidget = {
  id: string
  title: string
  has_value: boolean
}

export type DatoxKpiComparisonOptionDashboard = {
  id: string
  name: string
  widgets: DatoxKpiComparisonOptionWidget[]
}

export type DatoxKpiComparisonOptions = {
  previous_period: { id: string; label: string } | null
  dashboards: DatoxKpiComparisonOptionDashboard[]
  status: "available" | "unavailable"
  reason: string | null
}

export type DatoxKpiComparisonConfig = {
  version: 1
  mode: "previous_period"
  previous_dashboard_id: string | null
  previous_widget_id: string | null
  visual: "bars" | "slope"
  change: "both" | "amount" | "percentage"
  direction: "higher_is_better" | "lower_is_better" | "neutral"
}

export type DatoxKpiComparisonDirection = DatoxKpiComparisonConfig["direction"]

export type DatoxKpiComparisonDashboard = DatoxKpiComparisonOptionDashboard

export type DatoxKpiComparisonItem = {
  widget_id: string
  status: "available" | "unavailable" | "needs_selection"
  code: string | null
  reason: string | null
  current_value: number
  previous_value: number | null
  amount_change: number | null
  percentage_change: number | null
  previous_dashboard_id: string | null
  previous_widget_id: string | null
}

export type DatoxKpiComparisons = {
  dashboard_id: string
  revision: number
  current_period: { id: string; label: string }
  previous_period: { id: string; label: string } | null
  items: DatoxKpiComparisonItem[]
}

export function listOutputRevisions(projectID: string, outputID: string, kind: "report" | "dashboard") {
  return requestJson<DatoxOutputRevision[]>(`/api/projects/${projectID}/${kind === "report" ? "artifacts" : "dashboards"}/${outputID}/revisions`)
}

export function getDashboard(projectID: string, dashboardID: string, revision?: number) {
  return requestJson<DatoxDashboard>(`/api/projects/${projectID}/dashboards/${dashboardID}${revision ? `?revision=${revision}` : ""}`)
}

export function getDashboardKpiComparisonOptions(projectID: string, dashboardID: string) {
  return requestJson<DatoxKpiComparisonOptions>(
    `/api/projects/${projectID}/dashboards/${dashboardID}/kpi-comparison-options`,
  )
}

export async function compareDashboardKpis(
  projectID: string,
  dashboardID: string,
  body: {
    widget_ids: string[]
    current_values: Record<string, number>
    expected_revision: number
    selections: Record<string, { previous_dashboard_id: string | null; previous_widget_id: string | null }>
  },
) {
  return requestComparisonJson<DatoxKpiComparisons>(
    `/api/projects/${projectID}/dashboards/${dashboardID}/kpi-comparisons`,
    {
      method: "POST",
      headers: await jsonHeaders(),
      body: JSON.stringify(body),
    },
  )
}

export class DatoxComparisonError extends Error {
  constructor(
    public status: number,
    public detail: unknown,
  ) {
    super("datox-comparison-error")
    this.name = "DatoxComparisonError"
  }
}

export async function listArtifactsForPeriod(projectID: string, periodID?: string | null, artifactType?: string, caseID?: string) {
  const params = new URLSearchParams()
  if (periodID) params.set("period_id", periodID)
  if (artifactType) params.set("artifact_type", artifactType)
  if (caseID) params.set("case_id", caseID)
  const query = params.toString() ? `?${params.toString()}` : ""
  return requestJson<DatoxArtifact[]>(`/api/projects/${projectID}/artifacts${query}`)
}

export async function listDashboards(projectID: string, periodID?: string | null, caseID?: string, options?: { strict?: boolean }) {
  const query = workflowScopeQuery(periodID, caseID)
  const request = requestJson<DatoxDashboard[]>(`/api/projects/${projectID}/dashboards${query}`)
  return options?.strict ? request : request.catch(() => [])
}

export async function createDashboard(projectID: string, body: {
  case_id?: string
  period_id?: string | null
  name: string
  description?: string | null
  draft_config?: Record<string, unknown>
  dashboard_metadata?: Record<string, unknown>
}) {
  return requestJson<DatoxDashboard>(`/api/projects/${projectID}/dashboards`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify(body),
  })
}

export async function updateDashboard(projectID: string, dashboardID: string, body: {
  expected_revision?: number
  name?: string
  description?: string | null
  draft_config?: Record<string, unknown>
  dashboard_metadata?: Record<string, unknown>
  status?: string
}) {
  return requestJson<DatoxDashboard>(`/api/projects/${projectID}/dashboards/${dashboardID}`, {
    method: "PUT",
    headers: await jsonHeaders(),
    body: JSON.stringify(body),
  })
}

export async function explainDashboardWidgetSource(projectID: string, dashboardID: string, widgetID: string, tabID: string, signal?: AbortSignal) {
  return requestJson<DatoxDashboardSourceExplanation>(
    `/api/projects/${projectID}/dashboards/${dashboardID}/widgets/${widgetID}/explain-source`,
    {
      method: "POST",
      headers: await jsonHeaders(),
      body: JSON.stringify({ tab_id: tabID, source_rows_limit: 10 }),
      signal,
    },
  )
}

export async function deleteDashboard(projectID: string, dashboardID: string) {
  return requestJson<DatoxDashboard>(`/api/projects/${projectID}/dashboards/${dashboardID}`, {
    method: "DELETE",
    headers: await jsonHeaders(),
  })
}

export async function publishDashboard(projectID: string, dashboardID: string, publishedConfig?: Record<string, unknown>) {
  return requestJson<DatoxDashboard>(`/api/projects/${projectID}/dashboards/${dashboardID}/publish`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({ published_config: publishedConfig }),
  })
}

export async function unpublishDashboard(projectID: string, dashboardID: string) {
  return requestJson<DatoxDashboard>(`/api/projects/${projectID}/dashboards/${dashboardID}/unpublish`, {
    method: "POST",
    headers: await jsonHeaders(),
  })
}

export async function shareDashboardAccess(projectID: string, dashboardID: string, body: { approver_emails: string[]; reviewer_names?: Record<string, string>; client_values?: Record<string, string>; message?: string; published_config?: Record<string, unknown> }) {
  return requestJson<{ dashboard_id: string; url: string; approver_emails: string[] }>(`/api/projects/${projectID}/dashboards/${dashboardID}/share-access`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify(body),
  })
}

export async function listDashboardDatasets(projectID: string, periodID?: string | null, caseID?: string) {
  const query = workflowScopeQuery(periodID, caseID)
  return requestJson<DatoxDashboardDataset[]>(`/api/projects/${projectID}/dashboards/datasets${query}`).catch(() => [])
}

export async function previewDashboardDataset(projectID: string, datasetID: string, periodID?: string | null, limit = 100, caseID?: string, offset = 0, dashboardID?: string, expectedVersion?: string) {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) })
  if (periodID) params.set("period_id", periodID)
  if (caseID) params.set("case_id", caseID)
  if (dashboardID) params.set("dashboard_id", dashboardID)
  if (expectedVersion) params.set("expected_version", expectedVersion)
  return requestJson<DatoxDashboardDatasetPreview & { data_version?: string }>(`/api/projects/${projectID}/dashboards/datasets/${encodeURIComponent(datasetID)}/preview?${params.toString()}`)
}

export async function loadDashboardDataset(projectID: string, dashboardID: string, datasetID: string, periodID?: string | null, caseID?: string) {
  const first = await previewDashboardDataset(projectID, datasetID, periodID, 500, caseID, 0, dashboardID)
  if (!Number.isSafeInteger(first.row_count) || first.row_count < 0 || first.row_count > 100000) throw new Error("Dashboard dataset exceeds supported size")
  const rows = [...first.rows]
  while (rows.length < first.row_count) {
    const page = await previewDashboardDataset(projectID, datasetID, periodID, 500, caseID, rows.length, dashboardID, first.data_version)
    if (!page.rows.length || page.row_count !== first.row_count || page.data_version !== first.data_version || JSON.stringify(page.columns) !== JSON.stringify(first.columns)) throw new Error("Dashboard dataset changed during loading")
    rows.push(...page.rows)
  }
  if (rows.length !== first.row_count) throw new Error("Incomplete dashboard dataset")
  return { ...first, rows }
}

export type DatoxKpiResult = {
  version: 1
  status: "ready" | "unavailable" | "invalid"
  value: number | null
  mode: "formula" | "aggregation"
  row_count: number
  referenced_columns: string[]
  source: { dataset_id: string | null; revision?: number | null; dashboard_id?: string; name?: string | null }
  evidence: Array<Record<string, string | number | boolean | null>>
  constraints: {
    dashboard_filters: Array<{ column: string; selected_values: string[] }>
    filters: Array<Record<string, unknown>>
    sort: { column: string; direction: string } | null
    row_limit: number | string | null
  }
  error?: { code: string; params: Record<string, string> }
}

export async function getPublishedDashboard(dashboardID: string, approvalID?: string) {
  const path = approvalID ? `/api/public/dashboards/${dashboardID}/share/${approvalID}` : `/api/public/dashboards/${dashboardID}`
  return requestJson<{ id: string; name: string; config: Record<string, unknown>; datasets: DatoxDashboardDataset[]; output_tables: Array<{ datasetID: string; name: string; headers: string[]; rows: Array<Record<string, string | number | boolean | null>> }> }>(path)
}

export async function createArtifact(projectID: string, body: {
  period_id?: string | null
  session_id?: string | null
  artifact_type?: string
  format: string
  title: string
  content: string
  content_type: string
  preview_html?: string | null
  artifact_metadata?: Record<string, unknown>
  status?: string
}) {
  return requestJson<DatoxArtifact>(`/api/projects/${projectID}/artifacts`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify(body),
  })
}

// `strict` (V2 Source files): a failed read throws instead of reading as no extractions; his callers keep the default.
export async function listExtractions(projectID: string, periodID?: string | null, caseID?: string, options?: { strict?: boolean }) {
  const query = workflowScopeQuery(periodID, caseID)
  const request = requestJson<DatoxExtraction[]>(`/api/projects/${projectID}/extractions${query}`)
  return options?.strict ? request : request.catch(() => [])
}

export async function listRunExtractions(projectID: string, periodID: string, runID: string) {
  const query = new URLSearchParams({ period_id: periodID, workflow_run_id: runID })
  return requestJson<DatoxExtraction[]>(`/api/projects/${encodeURIComponent(projectID)}/extractions?${query}`)
}

export async function listTransformations(projectID: string, periodID?: string | null, caseID?: string) {
  const query = workflowScopeQuery(periodID, caseID)
  return requestJson<DatoxTransformation[]>(`/api/projects/${projectID}/transformations${query}`).catch(() => [])
}

export async function listApprovals(projectID: string, periodID?: string | null, caseID?: string) {
  const query = workflowScopeQuery(periodID, caseID)
  return requestJson<DatoxApproval[]>(`/api/projects/${projectID}/approvals${query}`).catch(() => [])
}

export async function listCatalogApprovals() {
  return requestJson<DatoxCatalogApproval[]>("/api/approvals")
}

export async function getCatalogApproval(approvalID: string) {
  return requestJson<DatoxCatalogApproval>(`/api/approvals/${approvalID}`)
}

export async function requestApprovals(projectID: string, reportArtifactID: string, approverEmails: string[], message?: string) {
  return requestJson<DatoxApproval[]>(`/api/projects/${projectID}/approvals`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({ report_artifact_id: reportArtifactID, approver_emails: approverEmails, message }),
  })
}

export async function updateApprovalReviewer(projectID: string, approvalID: string, approverEmail: string) {
  return requestJson<DatoxApproval>(`/api/projects/${projectID}/approvals/${approvalID}`, {
    method: "PATCH",
    headers: await jsonHeaders(),
    body: JSON.stringify({ approver_email: approverEmail }),
  })
}

export async function deleteApprovalReviewer(projectID: string, approvalID: string) {
  const response = await fetch(`${datoxApiBaseUrl()}/api/projects/${projectID}/approvals/${approvalID}`, {
    method: "DELETE",
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await response.text())
}

export async function getApprovalReview(approvalID: string) {
  return requestJson<DatoxApprovalReview>(`/api/reviews/${approvalID}`, { headers: {} })
}

export async function downloadApprovalReviewReport(approvalID: string) {
  const response = await fetch(`${datoxApiBaseUrl()}/api/reviews/${approvalID}/download`)
  if (!response.ok) throw new Error(await response.text())
  return {
    blob: await response.blob(),
    filename: downloadFilename(response.headers.get("Content-Disposition")) || "report",
  }
}

export async function approveReview(approvalID: string, comments?: string) {
  return requestJson<DatoxApprovalReview>(`/api/reviews/${approvalID}/approve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ comments }),
  })
}

export async function rejectReview(approvalID: string, comments?: string) {
  return requestJson<DatoxApprovalReview>(`/api/reviews/${approvalID}/reject`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ comments }),
  })
}

export async function saveReviewComments(approvalID: string, comments?: string) {
  return requestJson<DatoxApprovalReview>(`/api/reviews/${approvalID}/comments`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ comments }),
  })
}

export async function downloadArtifact(projectID: string, artifactID: string, revision?: number) {
  const response = await fetch(`${datoxApiBaseUrl()}/api/projects/${projectID}/artifacts/${artifactID}/download${revision ? `?revision=${revision}` : ""}`, {
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await response.text())
  return {
    blob: await response.blob(),
    filename: downloadFilename(response.headers.get("Content-Disposition")) || "report",
  }
}

export async function exportArtifact(projectID: string, artifactID: string, format: "html" | "txt" | "json" | "csv" | "pdf", revision?: number) {
  const response = await fetch(`${datoxApiBaseUrl()}/api/projects/${projectID}/artifacts/${artifactID}/export?format=${encodeURIComponent(format)}${revision ? `&revision=${revision}` : ""}`, {
    headers: await authHeaders(),
  })
  if (!response.ok) {
    const error: unknown = await response.json().catch(() => undefined)
    throw new Error(error && typeof error === "object" && "detail" in error && typeof error.detail === "string" ? error.detail : "")
  }
  return {
    blob: await response.blob(),
    filename: downloadFilename(response.headers.get("Content-Disposition")) || `report.${format}`,
  }
}

export async function deleteArtifact(projectID: string, artifactID: string) {
  const response = await fetch(`${datoxApiBaseUrl()}/api/projects/${projectID}/artifacts/${artifactID}`, {
    method: "DELETE",
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await response.text())
}

export async function addProjectMember(projectID: string, identifier: string, role: "viewer" | "editor") {
  const body = identifier.includes("@") ? { email: identifier, role } : { user_id: identifier, role }
  return requestJson<DatoxProjectMember>(`/api/projects/${projectID}/members`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify(body),
  })
}

export async function removeProjectMember(projectID: string, userID: string) {
  const response = await fetch(`${datoxApiBaseUrl()}/api/projects/${projectID}/members/${userID}`, {
    method: "DELETE",
    headers: await authHeaders(),
  })
  if (!response.ok) throw new Error(await response.text())
}

export async function uploadFile(projectID: string, file: File, periodID?: string, caseID?: string, sessionID?: string) {
  const form = new FormData()
  form.append("file", file)
  const scope = workflowScopeQuery(periodID, caseID)
  const query = sessionID ? `${scope}${scope ? "&" : "?"}session_id=${encodeURIComponent(sessionID)}` : scope
  return requestJson<DatoxFile>(`/api/projects/${projectID}/files${query}`, {
    method: "POST",
    headers: await authHeaders(),
    body: form,
  })
}

export async function getSessionContextByRemote(remoteSessionID: string) {
  return requestJson<DatoxSessionContext>(`/api/sessions/remote/${remoteSessionID}/context`).catch(() => undefined)
}

export async function streamMessage(
  projectID: string,
  sessionID: string,
  content: string,
  onEvent: (event: DatoxStreamEvent) => void,
  options?: { signal?: AbortSignal; structuredPayload?: Record<string, unknown> },
) {
  const response = await fetch(`${datoxApiBaseUrl()}/api/projects/${projectID}/sessions/${sessionID}/messages/stream`, {
    method: "POST",
    headers: await jsonHeaders(),
    body: JSON.stringify({ content, structured_payload: options?.structuredPayload }),
    signal: options?.signal,
  })
  if (!response.ok) throw new Error(await response.text())
  if (!response.body) return

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  const read = async (buffer: string): Promise<void> => {
    const next = await reader.read()
    const chunk = buffer + (next.value ? decoder.decode(next.value, { stream: !next.done }) : "")
    const events = chunk.split("\n\n")
    const remainder = events.pop() ?? ""
    events.forEach((event) => parseSseEvent(event, onEvent))
    if (next.done) {
      parseSseEvent(remainder, onEvent)
      return
    }
    return read(remainder)
  }
  return read("")
}

async function dashboardAgentRequest<T>(projectID: string, dashboardID: string, path: string, init: RequestInit = {}, namespace = "agent") {
  const { DashboardAgentError } = await import("./dashboard-agent")
  const url = `${datoxApiBaseUrl()}/api/projects/${encodeURIComponent(projectID)}/dashboards/${encodeURIComponent(dashboardID)}/${namespace}/${path}`
  const request = async (force = false) => fetch(url, { ...init, headers: { ...await authHeaders(force), "Content-Type": "application/json" } })
  const first = await request()
  const response = first.status === 401 ? await request(true) : first
  if (!response.ok) throw new DashboardAgentError(response.status)
  return await response.json() as T
}

export function getDashboardConversation(projectID: string, dashboardID: string, signal?: AbortSignal) {
  return dashboardAgentRequest<import("./dashboard-agent").DashboardAgentTurn[]>(projectID, dashboardID, "conversation", { signal })
}

export function proposeDashboardChanges(projectID: string, dashboardID: string, body: { prompt: string; expected_revision: number; selected_widget_ids: string[]; active_tab_id?: string; widget_references?: import("./dashboard-sources").DashboardWidgetReference[] }, signal?: AbortSignal) {
  return dashboardAgentRequest<import("./dashboard-agent").DashboardAgentPreview>(projectID, dashboardID, "previews", { method: "POST", body: JSON.stringify(body), signal })
}

export function getDashboardProposal(projectID: string, dashboardID: string, previewID: string, signal?: AbortSignal) {
  return dashboardAgentRequest<import("./dashboard-agent").DashboardAgentPreview>(projectID, dashboardID, `previews/${encodeURIComponent(previewID)}`, { signal })
}

export function applyDashboardProposal(projectID: string, dashboardID: string, previewID: string) {
  return dashboardAgentRequest<import("./dashboard-agent").DashboardAgentApply>(projectID, dashboardID, `previews/${encodeURIComponent(previewID)}/apply`, { method: "POST", body: "{}" })
}

export function discardDashboardProposal(projectID: string, dashboardID: string, previewID: string) {
  return dashboardAgentRequest<{ preview_id: string; status: "discarded" }>(projectID, dashboardID, `previews/${encodeURIComponent(previewID)}/discard`, { method: "POST", body: "{}" })
}

export function explainDashboardSource(reference: import("./dashboard-sources").DashboardWidgetReference, projectID: string, useAI = false, signal?: AbortSignal, expectedRevision?: number) {
  return dashboardAgentRequest<import("./dashboard-sources").DashboardSourceExplanation>(projectID, reference.dashboard_id, `${encodeURIComponent(reference.widget_id)}/explain-source`, {
    method: "POST", signal, body: JSON.stringify({ tab_id: reference.tab_id, revision: reference.revision || undefined, expected_revision: expectedRevision, source_rows_limit: 5, use_ai: useAI }),
  }, "widgets")
}

function sourceFilePath(reference: import("./dashboard-sources").DashboardWidgetReference, fileID: string, view: string, locator?: { sheet?: string; row?: number; column?: number }) {
  const params = new URLSearchParams({ tab_id: reference.tab_id })
  if (reference.revision) params.set("revision", String(reference.revision))
  if (locator?.sheet) params.set("sheet", locator.sheet)
  if (locator?.row) params.set("row", String(locator.row))
  if (locator?.column) params.set("column", String(locator.column))
  return `${encodeURIComponent(reference.widget_id)}/source-files/${encodeURIComponent(fileID)}/${view}?${params}`
}

export function getDashboardSourceSheet(projectID: string, reference: import("./dashboard-sources").DashboardWidgetReference, fileID: string, locator: { sheet?: string; row?: number; column?: number }, signal?: AbortSignal) {
  return dashboardAgentRequest<import("./dashboard-sources").SourceSheet>(projectID, reference.dashboard_id, sourceFilePath(reference, fileID, "sheet", locator), { signal }, "widgets")
}

export async function getDashboardSourceContent(projectID: string, reference: import("./dashboard-sources").DashboardWidgetReference, fileID: string, signal?: AbortSignal) {
  const url = `${datoxApiBaseUrl()}/api/projects/${encodeURIComponent(projectID)}/dashboards/${encodeURIComponent(reference.dashboard_id)}/widgets/${sourceFilePath(reference, fileID, "content")}`
  const request = async (force = false) => fetch(url, { headers: await authHeaders(force), signal })
  const first = await request()
  const response = first.status === 401 ? await request(true) : first
  if (!response.ok) throw new Error("Source file unavailable")
  return response.blob()
}

export type DashboardKpiComparison = { status: string; code: string; current_value: number | null; previous_value: number | null; amount_change: number | null; percentage_change: number | null }
export type DashboardPreviousKpis = { current_period: { id: string; label: string } | null; previous_period: { id: string; label: string } | null;
  periods: { id: string; label: string }[]; dashboards: { id: string; name: string; widgets: { id: string; title: string; has_value: boolean }[] }[]; status: string }
export function previewDashboardKpi(projectID: string, dashboardID: string, widget: Record<string, unknown>, revision?: number, signal?: AbortSignal, activeFilters?: Record<string, unknown>[]) {
  return dashboardAgentRequest<{ value: number | string | null; formula: string | null; referenced_columns: string[]; row_count: number }>(projectID, dashboardID, "kpi-preview",
    { method: "POST", body: JSON.stringify({ widget, expected_revision: revision, active_filters: activeFilters || [] }), signal }, "analytics")
}
export function getPreviousDashboardKpis(projectID: string, dashboardID: string, signal?: AbortSignal) {
  return dashboardAgentRequest<DashboardPreviousKpis>(projectID, dashboardID, "previous-kpis", { signal }, "analytics")
}
export function compareDashboardKpi(projectID: string, dashboardID: string, widget: Record<string, unknown>, value: number, revision?: number, signal?: AbortSignal) {
  return dashboardAgentRequest<DashboardKpiComparison>(projectID, dashboardID, "kpi-comparison", { method: "POST", body: JSON.stringify({ widget, current_value: value, expected_revision: revision }), signal }, "analytics")
}
export function setDashboardPreviousPeriod(projectID: string, dashboardID: string, previous: string | null, expected: string | null, revision: number) {
  return dashboardAgentRequest<DashboardPreviousKpis>(projectID, dashboardID, "previous-period", { method: "PUT", body: JSON.stringify({ previous_period_id: previous, expected_previous_period_id: expected, expected_revision: revision }) }, "analytics")
}

async function requestJson<T>(path: string, init?: RequestInit) {
  const response = await fetch(`${datoxApiBaseUrl()}${path}`, {
    ...init,
    headers: init?.headers ?? (await authHeaders()),
  })
  if (response.status === 401 && !init?.headers) {
    const retry = await fetch(`${datoxApiBaseUrl()}${path}`, {
      ...init,
      headers: await authHeaders(true),
    })
    if (!retry.ok) throw new Error(await retry.text())
    return (await retry.json()) as T
  }
  if (!response.ok) throw new Error(await response.text())
  return (await response.json()) as T
}

async function requestComparisonJson<T>(path: string, init?: RequestInit) {
  const response = await fetch(`${datoxApiBaseUrl()}${path}`, {
    ...init,
    headers: init?.headers ?? (await authHeaders()),
  })
  if (response.ok) return (await response.json()) as T
  const body = await response.text().catch(() => "")
  throw new DatoxComparisonError(response.status, body)
}

function workflowScopeQuery(periodID?: string | null, caseID?: string) {
  const params = new URLSearchParams()
  if (periodID) params.set("period_id", periodID)
  if (caseID) params.set("case_id", caseID)
  return params.size ? `?${params}` : ""
}

function parseSseEvent(raw: string, onEvent: (event: DatoxStreamEvent) => void) {
  const data = raw
    .split("\n")
    .filter((line) => line.startsWith("data: "))
    .map((line) => line.slice(6))
    .join("\n")
  if (!data || data === "[DONE]") return
  onEvent(JSON.parse(data) as DatoxStreamEvent)
}

function downloadFilename(contentDisposition: string | null) {
  if (!contentDisposition) return ""
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition)
  if (utf8?.[1]) return decodeURIComponent(utf8[1])
  const match = /filename="?([^";]+)"?/i.exec(contentDisposition)
  return match?.[1] ?? ""
}

async function authHeaders(forceRefresh = false): Promise<Record<string, string>> {
  if (localDevAuthEnabled()) return localDevHeaders()
  const token = tokenProvider ? await tokenProvider(forceRefresh) : ""
  if (!token) return {}
  return {
    Authorization: `Bearer ${token}`,
    "X-Active-Company-Id": activeCompanyID(),
  }
}

function localDevAuthEnabled() {
  if (import.meta.env.VITE_DATOX_MSAL_CLIENT_ID) return false
  try {
    const url = new URL(datoxApiBaseUrl())
    return url.hostname === "localhost" || url.hostname === "127.0.0.1"
  } catch {
    return false
  }
}
import { activeCompanyID, localDevHeaders } from "@/datox/local-auth"
